import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useCreateOrganizationAccountMutation } from 'app/store/reducers/api/config-manager-cloud-services/store/account-profiles';
import { useSwitchAccount } from 'app/shared/hooks/account/useSwitchAccount';
import { sitemap } from 'app/shared/util/url-utils';

interface CreateOrganizationDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Create an organization account, then switch into it. */
export const CreateOrganizationDialog: React.FC<CreateOrganizationDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [createOrganization, { isLoading: creating }] = useCreateOrganizationAccountMutation();
  const { switchTo, isLoading: switching } = useSwitchAccount();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const busy = creating || switching;

  const handleClose = () => {
    if (busy) return;
    setName('');
    setError(null);
    onClose();
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      const account = await createOrganization({ name: trimmed }).unwrap();
      if (account?.id) {
        await switchTo(account.id);
      }
      setName('');
      onClose();
      navigate(sitemap.home);
    } catch (err: any) {
      setError(err?.data?.detail ?? 'Could not create the organization.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create organization</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          An organization account lets you invite teammates and manage shared resources.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          label="Organization name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleCreate();
          }}
          error={Boolean(error)}
          helperText={error ?? ' '}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={busy || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrganizationDialog;
